import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { getRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";

const InventoryContext = createContext();

export const InventoryProvider = ({ children }) => {
  const [inventory, setInventory] = useState({
    data: [],
    meta: null,
    error: null,
    loading: false,
  });

  const [statistics, setStatistics] = useState({
    data: null,
    error: null,
    loading: false,
  });

  const [filterOptions, setFilterOptions] = useState({
    search: "",
    currentPage: 1,
    totalPages: 1,
    category_id: "",
    status: "all", // Add status filter
    warehouse: "all", // "all", "office", "mechanic"
    showLowStock: false, // Show only low stock items
    status_warehouse: "", // Warehouse status filter: "low"
    is_active: true, // NEW
    orderBy: "created_at", // NEW
    sort: "desc", // NEW
  });

  // Build query string based on status
  const getEndpointAndQuery = () => {
    const baseQuery = {
      search: filterOptions.search || "",
      page: filterOptions.currentPage,
      category_id: filterOptions.category_id || "",
      include: "category",
      limit: 300,
      warehouse: filterOptions.warehouse || "all",
      status_warehouse:
        filterOptions.status === "low"
          ? "low"
          : filterOptions.status === "out"
          ? "out"
          : filterOptions.status_warehouse || "",
      "filter[is_active]":
        filterOptions.is_active !== "" ? filterOptions.is_active : undefined,
      orderBy: filterOptions.orderBy || "created_at", // NEW
      sort: filterOptions.sort || "desc", // NEW
    };

    if (filterOptions.status === "low") {
      return {
        endpoint: "/inventory",
        queryString: buildQueryString(baseQuery),
      };
    } else {
      // For "all", "good", "empty" - use main inventory endpoint
      // Note: "good" and "empty" filtering might need to be done client-side or with additional API parameters
      return {
        endpoint: "/inventory",
        queryString: buildQueryString(baseQuery),
      };
    }
  };

  const { endpoint, queryString } = getEndpointAndQuery();

  const handleSearch = (valueOrEvent) => {
    const value =
      typeof valueOrEvent === "string"
        ? valueOrEvent
        : valueOrEvent?.target?.value || "";
    setFilterOptions((prevState) => ({
      ...prevState,
      search: value,
      currentPage: 1,
    }));
  };

  const handleCategoryFilter = (category) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      category_id: category,
      currentPage: 1, // Reset to first page when filtering
    }));
  };

  const handleStatusFilter = (status) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      status: status,
    }));
  };

  const handleWarehouseFilter = (warehouse) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      warehouse: warehouse,
    }));
  };

  const handleLowStockFilter = (showLowStock) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      showLowStock: showLowStock,
    }));
  };

  const handleStatusWarehouseFilter = (statusWarehouse) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      status_warehouse: statusWarehouse,
    }));
  };

  const handleSortFilter = (orderBy, sort) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      orderBy,
      sort,
    }));
  };

  const handleActiveFilter = (isActive) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      is_active: isActive,
      currentPage: 1,
    }));
  };

  const fetchStatistics = useCallback(async () => {
    setStatistics((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      const response = await getRequest("/inventory/statistics");
      console.log("Statistics API Response:", response);

      if (response.success) {
        setStatistics({
          data: response.data,
          error: null,
          loading: false,
        });
      } else {
        throw new Error(response.message || "Failed to fetch statistics");
      }
    } catch (error) {
      console.error("Error fetching statistics:", error);
      setStatistics((prev) => ({
        ...prev,
        error: error.message || "Failed to fetch statistics",
        loading: false,
      }));
    }
  }, []);

  const fetchInventory = useCallback(
    async (retryCount = 0) => {
      // const MAX_RETRIES = 4;

      setInventory((prevState) => ({
        ...prevState,
        loading: true,
      }));

      try {
        // Make actual API call
        console.log(
          "Fetching inventory with endpoint:",
          endpoint,
          "query:",
          queryString
        );
        const response = await getRequest(`${endpoint}${queryString}`);
        console.log("API Response:", response);

        if (response.success) {
          let processedData = response.data;

          // If this is low-stock endpoint, transform the data to match expected format
          if (endpoint === "/inventory/low-stock") {
            // Group by inventory_id to create items with warehouse_stocks
            const itemMap = new Map();

            response.data.forEach((record) => {
              const itemId = record.inventory_id;

              if (!itemMap.has(itemId)) {
                itemMap.set(itemId, {
                  id: itemId,
                  name: record.name,
                  category_id: record.category?.id,
                  category: record.category,
                  unit: record.unit,
                  minimum_stock: record.minimum_stock,
                  current_stock_total: 0, // Will be calculated
                  warehouse_stocks: [],
                  status: record.status,
                });
              }

              const item = itemMap.get(itemId);

              // Add warehouse stock
              item.warehouse_stocks.push({
                warehouse_id: record.warehouse_id,
                warehouse_name: record.warehouse_name,
                warehouse_code: record.warehouse_code,
                stock: record.current_stock,
                minimum_stock: record.minimum_stock,
                status: record.status,
              });

              // Update total stock
              item.current_stock_total += record.current_stock;
            });

            processedData = Array.from(itemMap.values());
          }

          setInventory({
            data: processedData,
            meta: response.meta || response.pagination,
            error: null,
            loading: false,
          });

          setFilterOptions((prev) => ({
            ...prev,
            totalPages:
              response.meta?.last_page || response.pagination?.last_page,
          }));

          console.log(
            "Set inventory meta:",
            response.meta || response.pagination,
            "data length:",
            processedData?.length
          );
        } else {
          throw new Error(response.message || "Failed to fetch inventory");
        }
      } catch (error) {
        console.error("Error fetching inventory:", error);
        setInventory((prev) => ({
          ...prev,
          error: error.message || "Failed to fetch inventory",
          loading: false,
        }));
      }
    },
    [endpoint, queryString]
  );

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  const handlePageChange = (page) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      currentPage: page,
    }));
  };

  const totalPages = inventory.meta?.last_page || 1;
  console.log(
    "Total pages calculated:",
    totalPages,
    "from meta:",
    inventory.meta
  );

  const Pagination = usePaginationWithComponent({
    totalPages: totalPages,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
  });

  return (
    <InventoryContext.Provider
      value={{
        inventory,
        statistics,
        filterOptions,
        handleSearch,
        handleCategoryFilter,
        handleStatusFilter,
        handleWarehouseFilter,
        handleLowStockFilter,
        handleStatusWarehouseFilter,
        handleSortFilter,
        handleActiveFilter,
        refetch: fetchInventory,
        Pagination,
      }}
    >
      {children}
    </InventoryContext.Provider>
  );
};

export const useInventory = () => {
  const context = useContext(InventoryContext);
  if (!context) {
    throw new Error("useInventory must be used within InventoryProvider");
  }

  const fetchOptions = async (inputValue) => {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 300));

    // Return dummy data filtered by search input for autocomplete
    const dummyInventory = [
      { id: 1, name: "Obeng Set Philips", qr_code: "TOOL-001" },
    ];

    const filteredItems = dummyInventory.filter(
      (item) =>
        item.name.toLowerCase().includes(inputValue.toLowerCase()) ||
        item.qr_code.toLowerCase().includes(inputValue.toLowerCase())
    );

    return filteredItems.map((item) => ({
      value: item.id,
      label: `${item.name} (${item.qr_code})`,
    }));
  };

  return { ...context, fetchOptions };
};
